/**
 * Fixed-step simulation (pure, deterministic — no Babylon, no DOM). The render layer reads state
 * and drains `events` each frame. One step = dt seconds (tests use 1/120).
 */
import {
  applyGate,
  BLAST_REACH_Z,
  BLASTS,
  bossUsesFireCycle,
  ENEMY_KILL_INTERVAL,
  ENEMY_LOOKAHEAD,
  GATE_VALUE_CAP,
  hazardOverlaps,
  HELI_ROCKET_DZ,
  PICKUP_AHEAD,
  PICKUP_HALF_X,
  SLAM_ZONES,
  TANK_MUZZLE_DZ,
  TANK_ZONE_HALF_X,
  squadHalfX,
  steerLimit,
  streamOffset,
  streamsFor,
  WALL_ATTRITION,
  type BossSpec,
  type GateSpec,
  type LevelSpec,
  type ObstacleKind,
  type WeaponSpec,
} from './level';

export interface Bullet {
  x: number;
  z: number;
  alive: boolean;
  /** Weapon tier at fire time: a bullet keeps the damage/speed it was fired with (advanceBullets
   *  resolves per bullet, so a mid-flight upgrade never re-arms the in-flight volley). */
  tier: number;
}

export interface SimGate {
  side: 'left' | 'right';
  spec: GateSpec;
  /** Numeric gates: the live (shootable) value. */
  value: number;
  z: number;
  consumed: boolean;
}

export interface SimCrate {
  z: number;
  x: number;
  hp: number;
  broken: boolean;
  /** Sim time of the break; null until broken (plan 08 S0). */
  brokenAt: number | null;
}

export interface SimEnemy {
  z: number;
  x: number;
  halfWidth: number;
  dropReward?: boolean;
  killInterval?: number;
  count: number;
  engaged: boolean;
  /** Suppression cooldown (dug-in cover: ENEMY_KILL_INTERVAL between kills under fire). */
  killCooldown: number;
  /**
   * Plan 13 firepower pickup at (x, z + PICKUP_AHEAD): 'live' once the block is wiped out before
   * contact, then 'taken' or 'missed' when the squad crosses it.
   */
  drop: 'none' | 'live' | 'taken' | 'missed';
}

export interface SimHazard {
  z: number;
  x: number;
  halfWidth: number;
  penalty: number;
  /** One-shot latch: set the first time the squad crosses this hazard's z (plan 04 P1.4). */
  latched: boolean;
  /** True when the latch actually cost soldiers (latched without overlap = cleanly dodged). */
  hit: boolean;
}

/** Plan 09: index-aligned with `level.obstacles` (like crates), so z keys never alias. */
export interface SimObstacle {
  kind: ObstacleKind;
  z: number;
  x: number;
  halfWidth: number;
  /** Remaining hp; set to 0 on break whatever the cause (bullets, blast or contact). */
  hp: number;
  broken: boolean;
  /** Sim time of the break; null until broken. */
  brokenAt: number | null;
  /** One-shot contact latch: set the first time the squad crosses this z in the run phase. */
  passed: boolean;
}

export type SimPhase = 'run' | 'boss' | 'won' | 'lost';

/** Every event carries the sim time it happened (plan 08 S0): render/audio read `sim.t - t`, never wall-clock. */
export type SimEvent =
  | { t: number; type: 'gateShot'; side: 'left' | 'right'; z: number; value: number; x?: number }
  | { t: number; type: 'gatePass'; side: 'left' | 'right'; z: number; n: number }
  | { t: number; type: 'crateHit'; z: number; x: number }
  | { t: number; type: 'crateBreak'; z: number; x: number; weapon: number }
  | { t: number; type: 'enemyKill'; z: number; remaining: number }
  | { t: number; type: 'enemyContact'; z: number; lost: number; n: number }
  | { t: number; type: 'hazardHit'; z: number; penalty: number; n: number }
  /** Plan 09: one `obstacleBreak` per obstacle (`lost` > 0 only for wall contact); explosives also
   *  emit `explosion`, whose `lost` is what that blast cost the squad. */
  | { t: number; type: 'obstacleHit'; z: number; x: number; hp: number }
  | { t: number; type: 'obstacleBreak'; z: number; x: number; kind: ObstacleKind; lost: number; n: number }
  | { t: number; type: 'explosion'; z: number; x: number; halfWidth: number; lost: number; n: number }
  | { t: number; type: 'bossHit'; hp: number; x?: number }
  | { t: number; type: 'slamTelegraph'; zone: [number, number] }
  | { t: number; type: 'slamImpact'; zone: [number, number]; hit: boolean; n: number }
  // Plan 15 tank events (design §4). bossFire: `zone` is the locked band, `x` the lateral
  // MUZZLE x at fire (fix round: `tankMuzzleXu` — the frozen-band aim walked out along the
  // barrel, so the FX burst + audio pan ride the gun; the rendered tracer re-derives the full
  // muzzle pose itself), `z` the ABSOLUTE muzzle z = boss.z + TANK_MUZZLE_DZ. Plan 19 D1 heli:
  // no barrel — `x` is the strafe x (bossStrafeX) and `z` = boss.z + HELI_ROCKET_DZ. shellImpact:
  // `z` = tankBandZ(spec), the squad's frozen boss-phase z — the shell lands ON the soldiers it
  // threatens.
  | { t: number; type: 'bossFire'; zone: [number, number]; x: number; z: number }
  | { t: number; type: 'shellImpact'; zone: [number, number]; hit: boolean; n: number; z: number }
  | { t: number; type: 'weaponUp'; tier: number }
  /** Plan 11: a rate pip gained (RATE+ gate) or stripped (landed slam); `pips` is the new count. */
  | { t: number; type: 'weaponMod'; pips: number; gained: boolean }
  | { t: number; type: 'won'; n: number }
  | { t: number; type: 'lost'; z: number };

export interface SlamState {
  /** Timer since the slam cycle started; phases: silent → telegraph → impact. Tank (plan 15):
   *  silent → telegraph (locked band) → inFlight (shell flying) → back to silent at detonation. */
  t: number;
  zone: [number, number];
  active: 'silent' | 'telegraph' | 'impact' | 'inFlight';
}

export interface SimState {
  t: number;
  phase: SimPhase;
  squadX: number;
  squadZ: number;
  n: number;
  weaponTier: number;
  /** Plan 11: rate pips from RATE+ gates (each +RATE_PIP fire rate); a landed slam strips one. */
  ratePips: number;
  fireCooldown: number;
  bullets: Bullet[];
  gates: SimGate[];
  crates: SimCrate[];
  enemies: SimEnemy[];
  hazards: SimHazard[];
  obstacles: SimObstacle[];
  bossHp: number;
  bossMaxHp: number;
  slam: SlamState | null;
  /** Sim time of the last slam impact (camera shake key); null until one lands. */
  slamAt: number | null;
  events: SimEvent[];
  /** Deterministic shot-pattern index (spread offsets alternate without RNG). */
  shotIndex: number;
  /** Plan 07 (skeletal prototype): sim-time of the last boss hp decrease, −1 before the first
   *  hit. Pure telemetry — the render layer derives the flinch pose from (t − bossHitAt), so no
   *  render-side latch is needed and paused frames stay reproducible. */
  bossHitAt: number;
  /** Plan 15 tank: sim-time of the last bossFire (the recoil/muzzle clock), −1 before the first
   *  fire. The bossHitAt pattern: the render layer guards `bossFireAt < 0` ⇒ rest pose (no
   *  recoil at t = 0 / restart) and derives the jolt from (t − bossFireAt) purely. */
  bossFireAt: number;
  /** Plan 27 (boss reveal): sim-time when the reveal window OPENED (the last BOSS_REVEAL_S of
   *  the approach), −1 before it opens and when the reveal was skipped (enemy threat at the
   *  threshold) or after a restart re-arms it. The bossHitAt pattern: pure telemetry — the
   *  audio director latches the −1 → t edge to duck the music bus, and the fire hold reads it
   *  directly. */
  bossRevealAt: number;
  /** Plan 27: sim-time of the standoff-arrival step (the step that flips phase to 'boss'), −1
   *  until then. Same telemetry pattern: the director slams the boss layer on the −1 → t edge
   *  and cameraImpulse derives the entry kick from (t − bossEnterAt) purely; −1 ⇒ rest pose. */
  bossEnterAt: number;
  /** Plan 07: sim-time when the phase became 'won' (the boss death moment), null until then.
   *  Drives the boss death clip the same pure way. */
  wonAt: number | null;
  /** Plan 07: recent per-slot squad losses, oldest first. A loss is recorded whenever n DECREASES
   *  (gate arithmetic, enemy contact, hazards, boss slam) — the dying slots are always the HIGHEST
   *  indices, because the formation keeps the first n soldiers (Squad.update places [0, n)). The
   *  render layer plays a death clip on each entry's slot for a bounded hold, purely from
   *  (sim.t − entry.t). Bounded ring (LOSSES_CAP) keeps the state small; evicted corpses simply
   *  stop rendering, which matches the pre-skeletal behaviour (dead soldiers vanished). */
  losses: SquadLoss[];
  /** Plan 10: n before the most recent change of n, and the sim time of that change (−1 before
   *  any). Pure telemetry like `losses` — the render layer settles the crowd from (t − crowdAt). */
  crowdFrom: number;
  crowdAt: number;
}

/** One recorded squad loss (plan 07): the slot index that died, at sim time t. */
export interface SquadLoss {
  slot: number;
  t: number;
}

/** Max retained loss entries (plan 07). Gate arithmetic can drop dozens at once; the corpse hold
 *  is a few seconds, so a bounded ring is enough for every shipped level. */
export const LOSSES_CAP = 24;

export interface SimInput {
  /** Desired lateral position in [-1, 1]. */
  targetX: number;
}

/** Fire-rate bonus per rate pip (plan 11). Must stay < 0.286: 2 pips on the minigun (0.11 / 1.5 s)
 *  have to keep the interval above the recoil time constant `MOTION.recoilTau` = 0.07 s. */
export const RATE_PIP = 0.25;

/**
 * Seconds between volleys with rate pips applied (plan 11): the ONE cadence source for the sim and
 * the render sites that time muzzle flashes and recoil. Structural `{ fireInterval }[]` so the
 * pose layer's narrow weapon view typechecks. At 0 pips it divides by exactly 1 (bit-identical).
 */
export function effectiveFireInterval(
  weapons: readonly { fireInterval: number }[],
  s: { weaponTier: number; ratePips: number },
): number {
  return (weapons[s.weaponTier] ?? weapons[0]).fireInterval / (1 + RATE_PIP * s.ratePips);
}

/**
 * Plan 15 tank helpers — both PURE, both exported from sim.ts (one source for the sim event, the
 * telegraph quad, the tracer end, the FX burst and the render root x; test-asserted equal by the
 * render lanes). They live here rather than level.ts because their only callers are the sim's boss
 * update + hull hit test and the render snapshot readers, and sim.ts is where the BossSpec
 * behaviour (vs the level DATA) lives.
 */

/**
 * Fire-cycle boss strafe x at sim time t (plan 15 §4 design, plan 19 D1): `strafeHalfWidth ·
 * sin(2π t / strafePeriod)`, exactly 0 for the brute (and for any spec missing the strafe fields —
 * synthetic brute specs stay bit-identical). NOT state: tryHitBoss evaluates it with the step's own
 * sim.t and the render layer reads the same sim.t between steps (skew ≤ one SIM_DT of motion ≈
 * 0.012 x-units).
 */
export function bossStrafeX(spec: BossSpec, t: number): number {
  if (!bossUsesFireCycle(spec) || !spec.strafeHalfWidth || !spec.strafePeriod) return 0;
  return spec.strafeHalfWidth * Math.sin((2 * Math.PI * t) / spec.strafePeriod);
}

/**
 * World metres per x-unit — the SAME number as render/world.ts BRIDGE_HALF_WIDTH (4.5; sim
 * cannot import the render layer, so it is restated here and CROSS-PINNED by tank.test.ts's
 * muzzle agreement test). Only `tankMuzzleXu` needs the real-world aspect (the aim geometry).
 */
const WORLD_PER_X = 4.5;

/**
 * The tank's lateral MUZZLE x at the fire moment (impl-gate fix round): the turret is FROZEN on
 * the locked band when the gun goes off, so the muzzle pose is derivable in the sim — aim at
 * the band centre (the same atan2 the render's frozen `tankAimYaw` branch uses, kept in sync by
 * the src/render/tank.test.ts muzzle-agreement cross-pin), then walk |TANK_MUZZLE_DZ| out along
 * the barrel. Stamped on
 * `bossFire.x` so the FX burst rides the gun, not the hull centreline.
 */
export function tankMuzzleXu(spec: BossSpec, strafeX: number, zoneCentre: number): number {
  const yaw = -Math.atan2((zoneCentre - strafeX) * WORLD_PER_X, spec.standoff);
  return strafeX - Math.sin(yaw) * (-TANK_MUZZLE_DZ / WORLD_PER_X);
}

/**
 * The tank's locked band z (design §4): `boss.z − standoff` — the squad's FROZEN boss-phase z
 * (the whole fight holds the squad there), i.e. the shell lands ON the soldiers it threatens.
 * One source, four consumers: the shellImpact event, the telegraph quad, the tracer end, the FX
 * burst.
 */
export function tankBandZ(spec: BossSpec): number {
  return spec.z - spec.standoff;
}

export function createSim(level: LevelSpec): SimState {
  const gates: SimGate[] = [];
  for (const pair of level.gates) {
    gates.push({ side: 'left', spec: pair.left, value: pair.left.value, z: pair.z, consumed: false });
    gates.push({ side: 'right', spec: pair.right, value: pair.right.value, z: pair.z, consumed: false });
  }
  return {
    t: 0,
    phase: 'run',
    squadX: 0,
    squadZ: 0,
    n: level.startN,
    weaponTier: 0,
    ratePips: 0,
    fireCooldown: 0,
    bullets: [],
    gates,
    crates: level.crates.map((c) => ({ ...c, broken: false, brokenAt: null })),
    enemies: level.enemies.map((e) => ({ ...e, engaged: false, killCooldown: 0, drop: 'none' as const })),
    hazards: level.hazards.map((h) => ({ ...h, latched: false, hit: false })),
    obstacles: level.obstacles.map((o) => ({ ...o, broken: false, brokenAt: null, passed: false })),
    bossHp: level.boss.hp,
    bossMaxHp: level.boss.hp,
    slam: null,
    slamAt: null,
    events: [],
    shotIndex: 0,
    bossHitAt: -1,
    bossFireAt: -1,
    bossRevealAt: -1,
    bossEnterAt: -1,
    wonAt: null,
    losses: [],
    crowdFrom: level.startN,
    crowdAt: -1,
  };
}

/** Record a squad loss of (from − to) highest-index slots (plan 07). Deterministic ring. */
function recordLosses(sim: SimState, from: number, to: number): void {
  if (to >= from) return;
  for (let slot = from - 1; slot >= to; slot--) sim.losses.push({ slot, t: sim.t });
  if (sim.losses.length > LOSSES_CAP) sim.losses.splice(0, sim.losses.length - LOSSES_CAP);
}

export function stepSim(level: LevelSpec, sim: SimState, dt: number, input: SimInput): void {
  if (sim.phase === 'won' || sim.phase === 'lost') return;
  sim.t += dt;
  const n0 = sim.n;

  // Lateral movement toward the input target. Plan 22: the target clamps to the box-on-deck
  // range ±steerLimit(squadHalfX(n)) — at the limit the formation EDGE kisses the rail, so no
  // soldier ever walks beside the bridge. No hard clamp on squadX: an out-of-range centre (a +k
  // gate just widened the box at the rail) glides back at lateralSpeed inside this same step
  // (worst transient 0.23 x-units ≈ 105 ms, see steerLimit's doc); one step can never jump the
  // range because movement is bounded by lateralSpeed·dt.
  const prevX = sim.squadX;
  const limit = steerLimit(squadHalfX(sim.n));
  const target = Math.max(-limit, Math.min(limit, input.targetX));
  const maxStep = level.lateralSpeed * dt;
  sim.squadX += Math.max(-maxStep, Math.min(maxStep, target - sim.squadX));

  // Forward movement freezes at the boss standoff.
  if (sim.phase === 'run') {
    const prevSquadZ = sim.squadZ;
    sim.squadZ += level.speed * dt;
    // Plan 27 Phase 1 (boss reveal): the ONE-SHOT threshold latch on the CROSSING EDGE — the
    // step where the standoff distance first passes `level.speed × BOSS_REVEAL_S` (run-phase
    // speed is constant, so that distance IS the last BOSS_REVEAL_S seconds) decides ONCE:
    // open (bossRevealAt = sim.t) iff no enemy block sits inside the 15 m threat lookahead,
    // else skip permanently (the field stays −1 until a restart re-runs createSim). squadZ is
    // strictly increasing in run phase, so the edge holds on exactly one step — no extra state
    // field and no re-evaluation (r3 opus finding B: a late clear never re-opens the window).
    if (sim.bossRevealAt === -1) {
      const standoffZ = level.boss.z - level.boss.standoff;
      const threshold = level.speed * BOSS_REVEAL_S;
      const prevDistance = standoffZ - prevSquadZ;
      const distance = standoffZ - sim.squadZ;
      // Plan 27 fix round (r1 opus P2-G): the guard must see the WHOLE window, not just the 15 m HUD
      // lookahead — the window is `level.speed × BOSS_REVEAL_S` metres long, so a level faster than
      // 15 m/s would otherwise leave its far end unguarded (a squad that cannot fire meeting an
      // entrant it never looked for). Shipped levels run at 10 m/s, so Math.max is behavior-identical
      // today; it is the speed guard the plan asked to be stated.
      if (prevDistance > threshold && distance <= threshold && !enemyThreat(sim, Math.max(ENEMY_LOOKAHEAD, threshold))) {
        sim.bossRevealAt = sim.t;
      }
    }
    if (sim.squadZ >= level.boss.z - level.boss.standoff) {
      sim.squadZ = level.boss.z - level.boss.standoff;
      sim.phase = 'boss';
      // Plan 27 Phase 1: the arrival step stamps the slam clock (−1 → t edge, the bossHitAt
      // pattern — the director slams the boss layer here and cameraImpulse kicks off it) and
      // DISCHARGES the held volley: `fire()` below already runs with phase 'boss', so zeroing
      // the frozen cooldown lands the first shot ON this step. Genre rule (plan 27 Goal 4): the
      // dodge check above still taxes the flip step — a squad steering hard on arrival gets its
      // volley deferred to the first non-dodge step (≤ ~0.1 s later), cooldown parked at 0.
      sim.bossEnterAt = sim.t;
      if (sim.bossRevealAt >= 0) sim.fireCooldown = 0;
      sim.slam = { t: 0, zone: level.boss.fairAttackZones ? [0.2, 1] : slamZone(0), active: 'silent' };
    }
  }

  // Evaluated after the phase advance: the step that arrives at the standoff is already a boss
  // step, and so is taxed like one.
  const dodging = sim.phase === 'boss' && Math.abs(sim.squadX - prevX) > DODGE_FIRE_PAUSE_X;

  fire(level, sim, dt, dodging);
  for (const enemy of sim.enemies) {
    if (enemy.killCooldown > 0) enemy.killCooldown = Math.max(0, enemy.killCooldown - dt);
  }
  advanceBullets(level, sim, dt);
  checkGatePass(level, sim);
  checkObstacleContact(sim);
  checkEnemyContact(sim);
  checkPickups(level, sim);
  checkHazards(sim);
  updateBoss(level, sim, dt);

  if (sim.n <= 0) {
    sim.n = 0;
    sim.phase = 'lost';
    emit(sim, { type: 'lost', z: sim.squadZ });
  }
  if (sim.n !== n0) {
    sim.crowdFrom = n0;
    sim.crowdAt = sim.t;
  }
}

const SPREAD: Record<number, number[]> = {
  1: [0],
  2: [-0.06, 0.06],
  3: [-0.09, 0, 0.09],
};

/**
 * Lateral movement that counts as a dodge (plan 04 P1.8), in x-units per 1/120 s step.
 * Above finger/trackpad jitter, below any real steer: the squad's full-speed step is
 * `lateralSpeed × dt` = 2.2/120 ≈ 0.0183 x-units, so a committed dodge always clears this bar and
 * a resting thumb never reaches it.
 */
export const DODGE_FIRE_PAUSE_X = 0.005;

/** Plan 27 (boss reveal): the reveal window's length in seconds of run-phase approach — the
 *  window opens `level.speed × BOSS_REVEAL_S` metres before the boss standoff (run-phase speed
 *  is constant, so distance ⇒ time exactly). Exported for the sim's threshold latch and the
 *  tests that pin the window math on every shipped level. */
export const BOSS_REVEAL_S = 1.0;

/**
 * Boss-phase fire pause (plan 04 P1.8, genre rule 9 — RW's "dodging stops your shooting").
 * Deliberately narrow: only the boss fight, and only on the steps in which the squad actually
 * moved laterally. Run-phase steering never stops fire (that would tax every gate lane-change and
 * re-derive the whole level balance); the volley is DEFERRED, not cancelled, so a dodge costs the
 * time the steering takes rather than a lost shot.
 */
function fire(level: LevelSpec, sim: SimState, dt: number, pausedForDodge: boolean): void {
  // Plan 27 Phase 1 (boss reveal): hold fire through the reveal window — the squad goes silent
  // WITH the music bus (the ask: "the soldiers stop shooting too"). Same DEFER shape as the
  // dodge branch verbatim (cooldown frozen ≥ 0, never cancelled; in-flight bullets keep
  // flying), so the arrival step's `fireCooldown = 0` discharges the whole held cadence.
  const revealHold = sim.bossRevealAt >= 0 && sim.phase === 'run';
  if (pausedForDodge || revealHold) {
    if (sim.fireCooldown < 0) sim.fireCooldown = 0; // never let a held cooldown go negative
    return;
  }
  sim.fireCooldown -= dt;
  const weapon = level.weapons[sim.weaponTier];
  const interval = effectiveFireInterval(level.weapons, sim);
  while (sim.fireCooldown <= 0) {
    sim.fireCooldown += interval;
    const spread = SPREAD[weapon.bulletsPerShot] ?? [0];
    // Plan 10 B1: every firing column fires the full volley; shotIndex still counts volleys.
    const streams = streamsFor(sim.n);
    for (let k = 0; k < streams; k++) {
      for (let i = 0; i < weapon.bulletsPerShot; i++) {
        const offset = spread[(sim.shotIndex + i) % spread.length] + streamOffset(k, streams);
        sim.bullets.push({ x: clamp(sim.squadX + offset, -1, 1), z: sim.squadZ + 2, alive: true, tier: sim.weaponTier });
      }
    }
    sim.shotIndex++;
  }
  if (sim.fireCooldown < 0) sim.fireCooldown = 0;
}

function advanceBullets(level: LevelSpec, sim: SimState, dt: number): void {
  const range = 110;
  for (const b of sim.bullets) {
    if (!b.alive) continue;
    // Per-bullet weapon (the tier it was fired with), not the squad's current one.
    const weapon = level.weapons[b.tier]!;
    const speed = weapon.bulletSpeed;
    const nextZ = b.z + speed * dt;
    // Order matters: gates → crates → obstacles → enemies → boss, each consumes the bullet on hit.
    // It only breaks ties inside one step's travel (≈ 0.58 m); across steps the nearest z wins.
    if (tryHitGate(sim, b, nextZ)) continue;
    if (tryHitCrate(level, sim, b, nextZ, weapon)) continue;
    if (tryHitObstacle(sim, b, nextZ, weapon)) continue;
    if (tryHitEnemy(sim, b, nextZ, weapon)) continue;
    if (tryHitBoss(level, sim, b, nextZ, weapon)) continue;
    b.z = nextZ;
    if (b.z > sim.squadZ + range) b.alive = false;
  }
  // Compact the pool occasionally (keeps arrays small without per-frame filtering).
  if (sim.bullets.length > 512) sim.bullets = sim.bullets.filter((b) => b.alive);
}

function crossed(from: number, to: number, at: number): boolean {
  return from < at && to >= at;
}

function tryHitGate(sim: SimState, b: Bullet, nextZ: number): boolean {
  for (const gate of sim.gates) {
    if (gate.consumed || gate.spec.kind !== 'num') continue;
    if (!crossed(b.z, nextZ, gate.z)) continue;
    // Same side rule as checkGatePass (`squadX < 0 ? 'left' : 'right'`): x = 0 belongs to the
    // RIGHT gate, so a centre-line bullet must raise the gate the squad will actually pass.
    if (gate.side === 'left' ? b.x >= 0 : b.x < 0) continue;
    // A bullet arrival raises the printed value by 1 (negatives tick toward positive). RAISE-ONLY:
    // a gate already printed above the cap keeps its value (plan 04 P1.2 — Math.min used to clamp
    // a +60 gate down to 10 on the first hit, which silently destroyed every large add gate).
    if (gate.value < GATE_VALUE_CAP) gate.value = Math.min(GATE_VALUE_CAP, gate.value + 1);
    b.alive = false;
    emit(sim, { type: 'gateShot', side: gate.side, z: gate.z, value: gate.value, x: b.x });
    return true;
  }
  return false;
}

type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

function emit(sim: SimState, ev: DistributiveOmit<SimEvent, 't'>): void {
  sim.events.push({ ...ev, t: sim.t } as SimEvent);
}

function tryHitCrate(level: LevelSpec, sim: SimState, b: Bullet, nextZ: number, weapon: WeaponSpec): boolean {
  const damage = weapon.damage * splashOf(weapon);
  for (const crate of sim.crates) {
    if (crate.broken || !crossed(b.z, nextZ, crate.z)) continue;
    if (Math.abs(b.x - crate.x) > 0.35) continue;
    crate.hp -= damage;
    b.alive = false;
    if (crate.hp <= 0) {
      crate.broken = true;
      crate.brokenAt = sim.t;
      // The ladder is as long as the level's weapon list (was hardcoded to 2 — plan 04 P1.6).
      sim.weaponTier = Math.min(sim.weaponTier + 1, level.weapons.length - 1);
      emit(sim, { type: 'crateBreak', z: crate.z, x: crate.x, weapon: sim.weaponTier });
      emit(sim, { type: 'weaponUp', tier: sim.weaponTier });
    } else {
      emit(sim, { type: 'crateHit', z: crate.z, x: crate.x });
    }
    return true;
  }
  return false;
}

/** Plan 09: same hit shape as an enemy block, same damage rule as a crate (bazooka splash doubles). */
function tryHitObstacle(sim: SimState, b: Bullet, nextZ: number, weapon: WeaponSpec): boolean {
  for (let i = 0; i < sim.obstacles.length; i++) {
    const o = sim.obstacles[i];
    if (o.broken || !crossed(b.z, nextZ, o.z) || Math.abs(b.x - o.x) > o.halfWidth) continue;
    b.alive = false;
    o.hp -= weapon.damage * splashOf(weapon);
    if (o.hp <= 0) breakObstacle(sim, i, 0);
    else emit(sim, { type: 'obstacleHit', z: o.z, x: o.x, hp: o.hp });
    return true;
  }
  return false;
}

/**
 * Break obstacle `first` (plan 09); `lost` is the wall-contact bill already charged. Explosives
 * blast the x-interval [x ± r] within BLAST_REACH_Z: the squad (run phase, footprint overlap) loses
 * `squadLoss` per blast, enemy blocks `enemyKill`, other obstacles `damage` — and an explosive
 * broken that way chains through the FIFO queue in this same step, in index order (deterministic).
 * Crates, gates and the boss are never hit.
 */
function breakObstacle(sim: SimState, first: number, lost: number): void {
  const queue = [first];
  while (queue.length > 0) {
    const i = queue.shift()!;
    const o = sim.obstacles[i];
    if (o.broken) continue;
    o.broken = true;
    o.brokenAt = sim.t;
    o.hp = 0;
    emit(sim, { type: 'obstacleBreak', z: o.z, x: o.x, kind: o.kind, lost: i === first ? lost : 0, n: sim.n });
    const blast = BLASTS[o.kind];
    if (!blast) continue;
    const lo = o.x - blast.halfWidth;
    const hi = o.x + blast.halfWidth;
    const nBefore = sim.n;
    const halfX = squadHalfX(sim.n);
    if (sim.phase === 'run' && Math.abs(o.z - sim.squadZ) <= BLAST_REACH_Z &&
      sim.squadX >= lo - halfX && sim.squadX <= hi + halfX) {
      sim.n = Math.max(0, sim.n - blast.squadLoss);
      recordLosses(sim, nBefore, sim.n);
    }
    emit(sim, { type: 'explosion', z: o.z, x: o.x, halfWidth: blast.halfWidth, lost: nBefore - sim.n, n: sim.n });
    for (const e of sim.enemies) {
      if (e.engaged || e.count <= 0 || Math.abs(e.z - o.z) > BLAST_REACH_Z) continue;
      if (e.x + e.halfWidth < lo || e.x - e.halfWidth > hi) continue;
      e.count = Math.max(0, e.count - blast.enemyKill);
      emit(sim, { type: 'enemyKill', z: e.z, remaining: e.count });
      dropIfWiped(e);
    }
    for (let j = 0; j < sim.obstacles.length; j++) {
      const other = sim.obstacles[j];
      if (other.broken || Math.abs(other.z - o.z) > BLAST_REACH_Z) continue;
      if (other.x + other.halfWidth < lo || other.x - other.halfWidth > hi) continue;
      other.hp -= blast.damage;
      if (other.hp <= 0) queue.push(j);
    }
  }
}

/**
 * Obstacle contact (plan 09): a one-shot latch on the z-cross, like hazards. Contact iff the
 * footprint overlaps the obstacle; an explosive detonates (its blast range contains the contact
 * range, so the squad is always inside it), a wall costs ceil(hp × WALL_ATTRITION) and breaks.
 * A passed but intact obstacle stays in the world: bullets only fly forward, but a blast can reach it.
 */
function checkObstacleContact(sim: SimState): void {
  if (sim.phase !== 'run') return;
  for (let i = 0; i < sim.obstacles.length; i++) {
    const o = sim.obstacles[i];
    if (o.passed || sim.squadZ < o.z) continue;
    o.passed = true;
    if (o.broken || Math.abs(sim.squadX - o.x) > o.halfWidth + squadHalfX(sim.n)) continue;
    if (BLASTS[o.kind]) {
      breakObstacle(sim, i, 0);
      continue;
    }
    const nBefore = sim.n;
    const lost = Math.min(sim.n, Math.ceil(o.hp * WALL_ATTRITION));
    sim.n -= lost;
    recordLosses(sim, nBefore, sim.n);
    breakObstacle(sim, i, lost);
  }
}

/** Same-target splash multiplier (plan 04 P1.6): 2 for the bazooka, 1 for small arms. */
function splashOf(weapon: WeaponSpec): number {
  return weapon.splash && weapon.splash > 1 ? weapon.splash : 1;
}

function tryHitEnemy(sim: SimState, b: Bullet, nextZ: number, weapon: WeaponSpec): boolean {
  for (const enemy of sim.enemies) {
    if (enemy.engaged || enemy.count <= 0) continue;
    if (!crossed(b.z, nextZ, enemy.z)) continue;
    if (Math.abs(b.x - enemy.x) > enemy.halfWidth) continue;
    b.alive = false;
    if (weapon.splash && weapon.splash > 1) {
      // Splash is the anti-block role: `splash` soldiers per connected shot, and the suppression
      // interval does not apply (a rocket does not stop at the front rank). One target only —
      // never a neighbouring lane.
      enemy.count = Math.max(0, enemy.count - weapon.splash);
      emit(sim, { type: 'enemyKill', z: enemy.z, remaining: enemy.count });
      dropIfWiped(enemy);
      return true;
    }
    // Campaign cover suppresses follow-up hits; exposed expedition groups accept every bullet.
    if (enemy.killCooldown <= 0) {
      enemy.count -= 1;
      enemy.killCooldown = enemy.killInterval ?? ENEMY_KILL_INTERVAL;
      emit(sim, { type: 'enemyKill', z: enemy.z, remaining: enemy.count });
      dropIfWiped(enemy);
    }
    return true;
  }
  return false;
}

function tryHitBoss(level: LevelSpec, sim: SimState, b: Bullet, nextZ: number, weapon: WeaponSpec): boolean {
  if (sim.phase !== 'boss') return false;
  const wall = level.boss.z - 2;
  if (!crossed(b.z, nextZ, wall)) return false;
  // Plan 15 tank / plan 19 D1 heli: the hull has moved off the bullet's line ⇒ MISS — the bullet
  // stays alive and dies at the 110 m range like any other. Evaluated with the step's own sim.t
  // (the same t advanceBullets sees), IDENTICAL evaluation for both fire-cycle kinds. The brute
  // branch below is unchanged (always hits — L1 fights bit-identical).
  if (bossUsesFireCycle(level.boss) &&
    Math.abs(b.x - bossStrafeX(level.boss, sim.t)) > (level.boss.hullHalfWidth ?? 0)) {
    return false;
  }
  sim.bossHp -= weapon.damage * splashOf(weapon);
  b.alive = false;
  sim.bossHitAt = sim.t;
  // Impl-gate fix round: the fire-cycle boss's bossHit sparks follow the hull (the strafing x at
  // the hit — tank plan 15, heli plan 19 D1); the brute leaves x unset so its event stream — and
  // the golden digest — stay byte-identical.
  const hitX = bossUsesFireCycle(level.boss) ? { x: bossStrafeX(level.boss, sim.t) } : {};
  emit(sim, { type: 'bossHit', hp: sim.bossHp, ...hitX });
  if (sim.bossHp <= 0) {
    sim.phase = 'won';
    sim.wonAt = sim.t;
    emit(sim, { type: 'won', n: sim.n });
  }
  return true;
}

function checkGatePass(level: LevelSpec, sim: SimState): void {
  if (sim.phase !== 'run') return;
  for (const pair of level.gates) {
    // Both gates of a pair are passed together (the pair is consumed at its z).
    const anyLeft = sim.gates.some((g) => g.z === pair.z && !g.consumed);
    if (!anyLeft) continue;
    if (sim.squadZ < pair.z) continue;
    const side: 'left' | 'right' = sim.squadX < 0 ? 'left' : 'right';
    const spec = side === 'left' ? pair.left : pair.right;
    const live = sim.gates.find((g) => g.z === pair.z && g.side === side)!;
    const applied: GateSpec = spec.kind === 'num' ? { kind: 'num', value: live.value } : spec;
    const nBefore = sim.n;
    sim.n = applyGate(sim.n, applied);
    recordLosses(sim, nBefore, sim.n);
    for (const g of sim.gates) if (g.z === pair.z) g.consumed = true;
    emit(sim, { type: 'gatePass', side, z: pair.z, n: sim.n });
    if (spec.kind === 'rate') {
      // Capped at 2 — the RATE_PIP contract (minigun interval must stay above MOTION.recoilTau).
      sim.ratePips = Math.min(2, sim.ratePips + spec.value);
      emit(sim, { type: 'weaponMod', pips: sim.ratePips, gained: true });
    }
  }
}

/** Plan 13: a block wiped out before contact drops its pickup (once). */
function dropIfWiped(enemy: SimEnemy): void {
  if (enemy.dropReward !== false && enemy.count <= 0 && !enemy.engaged && enemy.drop === 'none') enemy.drop = 'live';
}

/**
 * One-shot latch on the block's z. Contact (plan 13) only when the footprint overlaps the block's
 * x-range: a squad in the other lane walks past and loses nothing (`lost` 0, like a wiped block).
 */
function checkEnemyContact(sim: SimState): void {
  if (sim.phase !== 'run') return;
  for (const enemy of sim.enemies) {
    if (enemy.engaged || sim.squadZ < enemy.z) continue;
    enemy.engaged = true;
    const touching = Math.abs(sim.squadX - enemy.x) <= enemy.halfWidth + squadHalfX(sim.n);
    const lost = touching ? Math.min(sim.n, enemy.count) : 0;
    const nBefore = sim.n;
    sim.n -= lost;
    recordLosses(sim, nBefore, sim.n);
    enemy.count = 0;
    emit(sim, { type: 'enemyContact', z: enemy.z, lost, n: sim.n });
  }
}

/** Plan 13: walk over a live pickup to take it — next weapon tier, or a rate pip at the top. */
function checkPickups(level: LevelSpec, sim: SimState): void {
  if (sim.phase !== 'run') return;
  for (const enemy of sim.enemies) {
    if (enemy.drop !== 'live' || sim.squadZ < enemy.z + PICKUP_AHEAD) continue;
    if (Math.abs(sim.squadX - enemy.x) > PICKUP_HALF_X + squadHalfX(sim.n)) {
      enemy.drop = 'missed';
      continue;
    }
    enemy.drop = 'taken';
    if (sim.weaponTier < level.weapons.length - 1) {
      sim.weaponTier++;
      emit(sim, { type: 'weaponUp', tier: sim.weaponTier });
    } else {
      // Same 2-pip cap as the RATE+ gate branch above.
      sim.ratePips = Math.min(2, sim.ratePips + 1);
      emit(sim, { type: 'weaponMod', pips: sim.ratePips, gained: true });
    }
  }
}

/**
 * Static hazards (plan 04 P1.4): one-shot latch on the z-cross, damage only when the formation
 * footprint overlaps the band. Hazards never interact with bullets — they are ground-level props
 * below the torso-height firing line (advanceBullets does not know they exist).
 */
function checkHazards(sim: SimState): void {
  if (sim.phase !== 'run') return;
  for (const hazard of sim.hazards) {
    if (hazard.latched || sim.squadZ < hazard.z) continue;
    hazard.latched = true;
    if (!hazardOverlaps(hazard, sim.squadX, squadHalfX(sim.n))) continue;
    hazard.hit = true;
    // Clamped like applyGate does: the loss check below reads `sim.n <= 0`, so a penalty larger
    // than the formation must land on 0, not on a negative squad the event log has to explain.
    const nBeforeHazard = sim.n;
    sim.n = Math.max(0, sim.n - hazard.penalty);
    recordLosses(sim, nBeforeHazard, sim.n);
    emit(sim, { type: 'hazardHit', z: hazard.z, penalty: hazard.penalty, n: sim.n });
  }
}

function slamZone(index: number): [number, number] {
  return SLAM_ZONES[index % SLAM_ZONES.length];
}

/**
 * Brute slam cycle (plan 13, UNCHANGED — bit-identical): silent → telegraph at P − T → impact at
 * P, zone from the deterministic pattern. A slam lands when the squad's footprint overlaps the
 * zone, and a landed slam strips one rate pip (plan 11).
 */
function updateBruteBoss(level: LevelSpec, sim: SimState, slam: SlamState): void {
  const boss = level.boss;
  if (slam.active === 'silent' && slam.t >= boss.slamPeriod - boss.slamTelegraph) {
    slam.active = 'telegraph';
    slam.zone = boss.fairAttackZones
      ? (slam.zone[0] > 0 ? [-1, -0.2] : [0.2, 1])
      : slamZone(Math.floor(sim.t / boss.slamPeriod));
    emit(sim, { type: 'slamTelegraph', zone: [...slam.zone] as [number, number] });
  }
  if (slam.active === 'telegraph' && slam.t >= boss.slamPeriod) {
    slam.active = 'silent';
    slam.t = 0;
    const [lo, hi] = slam.zone;
    const halfX = squadHalfX(sim.n); // plan 13: the footprint, not the centre point, has to clear it
    const hit = sim.squadX + halfX >= lo && sim.squadX - halfX <= hi;
    if (hit) {
      const nBeforeSlam = sim.n;
      // Clamped like checkHazards: a fatal slam must land on 0, not a negative squad (negative
      // slots would make recordLosses log slots the render layer drops — no corpses on a wipe).
      sim.n = Math.max(0, sim.n - boss.slamDamage);
      recordLosses(sim, nBeforeSlam, sim.n);
      // Plan 11: a landed slam also strips one rate pip (partial loss, never a reset).
      if (sim.ratePips > 0) {
        sim.ratePips--;
        emit(sim, { type: 'weaponMod', pips: sim.ratePips, gained: false });
      }
    }
    sim.slamAt = sim.t;
    emit(sim, { type: 'slamImpact', zone: [lo, hi], hit, n: sim.n });
  }
}

/**
 * Tank fire cycle (plan 15 §4, phase-GATED — three guarded transitions, each requires the exact
 * predecessor phase, so the cycle is fire-once per period). Plan 19 D1: SHARED BY THE HELI —
 * `updateBoss` dispatches both fire-cycle kinds here; the only kind-dependent behaviour is the
 * bossFire event shape below (the heli's pods vs the tank's barrel-walk muzzle).
 *
 * silent → at `t ≥ P − T` AND `active === 'silent'`: LOCK — band of half-width TANK_ZONE_HALF_X
 *   centred on squadX (centre clamped to ±(1 − TANK_ZONE_HALF_X)), `slamTelegraph` emitted. The
 *   zone then NEVER moves until detonation (a literal ungated `≥` re-lock would chase the squad
 *   and defeat dodging).
 * telegraph → at `t ≥ P − F` AND `active === 'telegraph'`: FIRE — `inFlight`, bossFireAt stamped,
 *   `bossFire` emitted (exactly one per cycle; the F == T edge locks and fires in the same step,
 *   and F < SIM_DT still fires one gate before the detonation gate reads 'inFlight').
 * inFlight → at `t ≥ P` AND `active === 'inFlight'`: DETONATION — the landed-slam rule verbatim
 *   (footprint OVERLAP, never "inside"), `shellImpact` at z = tankBandZ(spec), then reset.
 *
 * stepSim early-returns on won/lost, so a shell whose boss dies mid-flight NEVER detonates —
 * including in the same step the boss dies.
 */
function updateTankBoss(level: LevelSpec, sim: SimState, slam: SlamState): void {
  const boss = level.boss;
  const flight = boss.shellFlight ?? 0;
  if (slam.active === 'silent' && slam.t >= boss.slamPeriod - boss.slamTelegraph) {
    const centre = boss.fairAttackZones
      ? (sim.squadX < 0 ? -0.7 : 0.7)
      : Math.max(-(1 - TANK_ZONE_HALF_X), Math.min(1 - TANK_ZONE_HALF_X, sim.squadX));
    slam.zone = [centre - TANK_ZONE_HALF_X, centre + TANK_ZONE_HALF_X];
    slam.active = 'telegraph';
    emit(sim, { type: 'slamTelegraph', zone: [...slam.zone] as [number, number] });
  }
  if (slam.active === 'telegraph' && slam.t >= boss.slamPeriod - flight) {
    slam.active = 'inFlight';
    sim.bossFireAt = sim.t;
    const strafe = bossStrafeX(boss, sim.t);
    const centre = (slam.zone[0] + slam.zone[1]) / 2;
    // Plan 19 D1: the heli fires from its pods — no barrel to walk out along, so x is the strafe
    // x itself and z rides HELI_ROCKET_DZ. The tank branch stays bit-identical.
    const heli = boss.kind === 'heli';
    emit(sim, {
      type: 'bossFire',
      zone: [...slam.zone] as [number, number],
      // Fix round: the MUZZLE x (frozen band aim), not the hull centre — the FX burst rides the gun.
      x: heli ? strafe : tankMuzzleXu(boss, strafe, centre),
      z: boss.z + (heli ? HELI_ROCKET_DZ : TANK_MUZZLE_DZ),
    });
  }
  if (slam.active === 'inFlight' && slam.t >= boss.slamPeriod) {
    const [lo, hi] = slam.zone;
    const halfX = squadHalfX(sim.n); // the landed-slam rule VERBATIM (footprint overlap)
    const hit = sim.squadX + halfX >= lo && sim.squadX - halfX <= hi;
    if (hit) {
      const nBeforeShell = sim.n;
      // Clamped like checkHazards: a fatal shell must land on 0, not a negative squad (negative
      // slots would make recordLosses log slots the render layer drops — no corpses on a wipe).
      sim.n = Math.max(0, sim.n - boss.slamDamage);
      recordLosses(sim, nBeforeShell, sim.n);
      // Reused landed-slam rule: a hit shell also strips one rate pip (partial loss, never a reset).
      if (sim.ratePips > 0) {
        sim.ratePips--;
        emit(sim, { type: 'weaponMod', pips: sim.ratePips, gained: false });
      }
    }
    sim.slamAt = sim.t;
    emit(sim, { type: 'shellImpact', zone: [lo, hi], hit, n: sim.n, z: tankBandZ(boss) });
    slam.active = 'silent';
    slam.t = 0;
  }
}

function updateBoss(level: LevelSpec, sim: SimState, dt: number): void {
  if (sim.phase !== 'boss' || !sim.slam) return;
  const slam = sim.slam;
  slam.t += dt;
  // Brute path bit-identical: the fire-cycle branch (plan 15 tank, plan 19 D1 heli) is guarded on
  // bossUsesFireCycle, the brute keeps its own two-phase cycle (silent → telegraph → impact via
  // the silent reset).
  if (bossUsesFireCycle(level.boss)) updateTankBoss(level, sim, slam);
  else updateBruteBoss(level, sim, slam);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Drain the event queue (the render layer calls this every frame). */
export function drainEvents(sim: SimState): SimEvent[] {
  const events = sim.events;
  sim.events = [];
  return events;
}

/**
 * The enemy block the HUD must account for (plan 04 P1.5): the nearest not-yet-engaged block with
 * soldiers left, inside `lookahead` metres ahead of the squad. Returns null when nothing is in
 * range, which is also "hide the M readout". This one function is the source of truth for both the
 * displayed M and the probe's copy of it, so the two can be compared frame-for-frame.
 */
export function enemyThreat(
  sim: SimState,
  lookahead = ENEMY_LOOKAHEAD,
): { z: number; x: number; count: number } | null {
  if (sim.phase === 'won' || sim.phase === 'lost') return null;
  let best: { z: number; x: number; count: number } | null = null;
  for (const enemy of sim.enemies) {
    if (enemy.engaged || enemy.count <= 0) continue;
    if (enemy.z <= sim.squadZ || enemy.z - sim.squadZ > lookahead) continue;
    if (!best || enemy.z < best.z) best = { z: enemy.z, x: enemy.x, count: enemy.count };
  }
  return best;
}
